import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import { MDBDataTable } from 'mdbreact';
import { toast } from 'react-toastify';
import Sidebar from '../Dashboard/Sidebar';
import Loader from '../layouts/Loader';
import { getAllBills } from '../../actions/billingActions';
import { clearBillingError } from '../../slices/billingSlice';
import { downloadBillPDF } from '../../utils/billPdf';
import formatDate from '../../utils/formatDate';
import InvoicePreviewModal from './InvoicePreviewModal';

const currency = (n) =>
  `₹${Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;

const statusStyle = (status) => {
  const styles = {
    Pending:          { background: 'rgba(100,116,139,0.12)', color: '#475569', border: '1px solid rgba(100,116,139,0.25)' },
    'Partially Paid': { background: 'rgba(245,158,11,0.12)', color: '#b45309', border: '1px solid rgba(245,158,11,0.3)' },
    Paid:             { background: 'rgba(16,185,129,0.12)', color: '#065f46', border: '1px solid rgba(16,185,129,0.3)' },
    Cancelled:        { background: 'rgba(239,68,68,0.12)',  color: '#991b1b', border: '1px solid rgba(239,68,68,0.3)' },
  };
  return styles[status] || styles.Pending;
};

const STATUS_FILTERS = ['All', 'Pending', 'Partially Paid', 'Paid', 'Cancelled'];

export default function AllBills() {
  const dispatch = useDispatch();
  const { bills = [], loading, error } = useSelector(state => state.billingState);
  const [statusFilter, setStatusFilter] = useState('All');
  const [previewBill, setPreviewBill] = useState(null);

  useEffect(() => {
    dispatch(getAllBills(statusFilter === 'All' ? undefined : statusFilter));
  }, [dispatch, statusFilter]);

  useEffect(() => {
    if (error) {
      toast(error, { type: 'error' });
      dispatch(clearBillingError());
    }
  }, [error, dispatch]);

  const totals = bills.reduce(
    (acc, b) => {
      if (b.status !== 'Cancelled') {
        acc.billed += b.totalAmount || 0;
        acc.paid   += b.amountPaid  || 0;
        acc.due    += b.balanceDue  || 0;
      }
      return acc;
    },
    { billed: 0, paid: 0, due: 0 }
  );

  const getPaymentMethod = (b) => {
    if (b.payments && b.payments.length > 0) {
      const last = b.payments[b.payments.length - 1];
      if (last && last.method) return last.method;
    }
    if (b.paymentMethod && b.paymentMethod !== '—' && String(b.paymentMethod).trim() !== '') {
      return b.paymentMethod;
    }
    return 'Cash';
  };

  const getDoctorName = (b) => {
    if (b.doctor && b.doctor !== '—' && String(b.doctor).trim() !== '') return b.doctor;
    if (b.patient?.visits && b.patient.visits.length > 0) {
      for (let i = b.patient.visits.length - 1; i >= 0; i--) {
        if (b.patient.visits[i].doctor && String(b.patient.visits[i].doctor).trim() !== '') {
          return b.patient.visits[i].doctor;
        }
      }
    }
    if (b.patient?.doctor && String(b.patient.doctor).trim() !== '') return b.patient.doctor;
    return 'Doctor';
  };

  const handleSendWhatsApp = (b) => {
    const patientObj = b.patient || {};
    const baseUrl = window.location.origin;
    const tokenParam = b.shareToken ? `?token=${encodeURIComponent(b.shareToken)}` : '';
    const invoiceLink = `${baseUrl}/invoice/${b._id || b.id}${tokenParam}`;
    const patientName = patientObj.name || 'Valued Patient';

    let cleanPhone = String(patientObj.phone || '').replace(/\D/g, '');
    if (cleanPhone.startsWith('0')) cleanPhone = cleanPhone.replace(/^0+/, '');
    if (cleanPhone.length === 10) cleanPhone = `91${cleanPhone}`;

    const websiteUrl = 'https://greennaturopathy.com';
    const reviewUrl = 'https://g.page/r/CfNAbnnuCG74EAI/review';
    const messageBody = `Dear ${patientName},\n\nThank you for visiting Green Naturopathy.\n\nYour official invoice (${b.invoiceNumber}) is ready.\n\n📄 View and download your invoice:\n${invoiceLink}\n\n⭐ Share your feedback / Rate us on Google:\n${reviewUrl}\n\n🌐 Visit our official website:\n${websiteUrl}\n\nHave a healthy and wonderful day!\n— Green Naturopathy`;
    const encodedText = encodeURIComponent(messageBody);

    const waUrl = cleanPhone
      ? `https://api.whatsapp.com/send?phone=${cleanPhone}&text=${encodedText}`
      : `https://api.whatsapp.com/send?text=${encodedText}`;
    window.open(waUrl, '_blank', 'noopener,noreferrer');
  };

  const setBillsData = () => {
    const data = {
      columns: [
        { label: 'Invoice #', field: 'invoiceNumber', sort: 'asc' },
        { label: 'Patient',   field: 'patient',       sort: 'asc' },
        { label: 'Doctor',    field: 'doctor',        sort: 'asc' },
        { label: 'Date',      field: 'date',          sort: 'asc' },
        { label: 'Total',     field: 'total',         sort: 'asc' },
        { label: 'Paid',      field: 'paid',          sort: 'asc' },
        { label: 'Balance Due', field: 'balanceDue',  sort: 'asc' },
        { label: 'Method of Payment', field: 'paymentMethod', sort: 'asc' },
        { label: 'Status',    field: 'status',        sort: 'asc' },
        { label: 'Actions',   field: 'actions',       sort: 'asc' },
      ],
      rows: [],
    };

    bills.forEach(b => {
      const dueAmt = Number(b.balanceDue || 0);
      data.rows.push({
        invoiceNumber: (
          <span className="fw-semibold" style={{ color: 'var(--primary)' }}>{b.invoiceNumber}</span>
        ),
        patient: b.patient?.name ? (
          <Link
            to={`/patient/${b.patient.patientId || b.patient._id || b.patient.id}`}
            className="fw-semibold text-decoration-none"
            style={{ color: 'var(--primary)' }}
            title="View Patient Profile"
          >
            {b.patient.name} {b.patient.patientId ? `(${b.patient.patientId})` : ''}
          </Link>
        ) : b.patientId,
        doctor: getDoctorName(b),
        date:    formatDate(b.createdAt),
        total:   currency(b.totalAmount),
        paid:    <span style={{ color: 'var(--success)', fontWeight: 600 }}>{currency(b.amountPaid)}</span>,
        balanceDue: (
          <span style={{
            color: dueAmt > 0 ? 'var(--danger)' : 'var(--text-muted)',
            fontWeight: dueAmt > 0 ? 600 : 500
          }}>
            {currency(dueAmt)}
          </span>
        ),
        paymentMethod: (
          <span className="badge bg-light text-dark border fw-medium">
            {getPaymentMethod(b)}
          </span>
        ),
        status: (
          <span
            style={{
              ...statusStyle(b.status),
              padding: '0.28em 0.7em',
              borderRadius: '6px',
              fontSize: '0.75rem',
              fontWeight: 600,
              display: 'inline-block',
            }}
          >
            {b.status}
          </span>
        ),
        actions: (
          <Fragment>
            <button
              type="button"
              className="btn btn-sm me-1"
              style={{
                background: 'rgba(59,130,246,0.1)',
                color: '#3b82f6',
                border: '1px solid rgba(59,130,246,0.25)',
                borderRadius: '6px',
              }}
              onClick={() => setPreviewBill(b)}
              title="View Invoice Preview (Without Download)"
            >
              <i className="fa fa-eye"></i>
            </button>
            <Link
              to={`/admin/billing/${b._id}/edit`}
              className="btn btn-sm me-1"
              style={{
                background: 'rgba(13,148,136,0.1)',
                color: 'var(--primary)',
                border: '1px solid rgba(13,148,136,0.25)',
                borderRadius: '6px',
              }}
            >
              <i className="fa fa-pencil"></i>
            </Link>
            <button
              className="btn btn-sm me-1"
              style={{
                background: 'rgba(13,148,136,0.1)',
                color: 'var(--primary)',
                border: '1px solid rgba(13,148,136,0.25)',
                borderRadius: '6px',
              }}
              onClick={() => downloadBillPDF(b, b.patient || {}, 'computer')}
              title="Download Invoice PDF"
            >
              <i className="fa fa-file-invoice"></i>
            </button>
            <button
              className="btn btn-sm me-1"
              style={{
                background: 'rgba(37,211,102,0.12)',
                color: '#128c7e',
                border: '1px solid rgba(37,211,102,0.3)',
                borderRadius: '6px',
              }}
              onClick={() => handleSendWhatsApp(b)}
              title="Send Invoice to Patient via WhatsApp"
            >
              <i className="fab fa-whatsapp"></i>
            </button>
            <button
              className="btn btn-sm"
              style={{
                background: 'rgba(100,116,139,0.1)',
                color: '#475569',
                border: '1px solid rgba(100,116,139,0.25)',
                borderRadius: '6px',
              }}
              onClick={() => downloadBillPDF(b, b.patient || {}, 'signature')}
              title="Download PDF for Physical Signature"
            >
              <i className="fa fa-file-signature"></i>
            </button>
          </Fragment>
        ),
      });
    });

    return data;
  };

  return (
    <div className="row g-0">
      <div className="col-12 col-md-2"><Sidebar /></div>
      <div className="col-12 col-md-10 main-content">
        {/* Page header */}
        <div className="d-flex justify-content-between align-items-center mb-4 flex-wrap gap-2">
          <div>
            <h1 className="mb-0 fw-bold" style={{ fontSize: '1.5rem' }}>Billing</h1>
            <p className="mb-0 mt-1" style={{ color: 'var(--text-muted)', fontSize: '0.875rem' }}>
              {bills.length} invoice{bills.length !== 1 ? 's' : ''}
            </p>
          </div>
          <Link to="/admin/billing/new" className="btn btn-primary">
            <i className="fa fa-plus me-2"></i>New Bill
          </Link>
        </div>

        {/* Summary tiles */}
        <div className="row g-2 g-md-3 mb-4 billing-summary-tiles">
          {[
            { label: 'Total Billed', value: currency(totals.billed), color: 'var(--text-dark)' },
            { label: 'Total Paid',   value: currency(totals.paid),   color: 'var(--success)' },
            { label: 'Balance Due',  value: currency(totals.due),    color: 'var(--danger)' },
          ].map(tile => (
            <div className="col-4" key={tile.label}>
              <div className="stat-tile">
                <div className="stat-label text-truncate" title={tile.label}>{tile.label}</div>
                <div className="stat-value text-truncate" title={tile.value} style={{ color: tile.color }}>{tile.value}</div>
              </div>
            </div>
          ))}
        </div>

        {/* Status filter pills */}
        <div className="filter-pill-group mb-3">
          {STATUS_FILTERS.map(s => (
            <button
              key={s}
              className={`filter-pill ${statusFilter === s ? 'active' : ''}`}
              onClick={() => setStatusFilter(s)}
            >
              {s}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="card">
          <div className="card-body p-3">
            {loading ? (
              <Loader />
            ) : (
              <MDBDataTable
                data={setBillsData()}
                bordered
                hover
                responsive
                className="px-2"
                style={{ fontSize: '0.875rem' }}
              />
            )}
          </div>
        </div>

        {/* Invoice Preview Modal */}
        <InvoicePreviewModal
          bill={previewBill}
          isOpen={Boolean(previewBill)}
          onClose={() => setPreviewBill(null)}
        />
      </div>
      </div>
  );
}



